import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, Zap, Users, Star, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import {
  gridContainerVariants,
  statsContainerVariants,
  statsItemVariants,
  getGridItemVariants,
} from '@/lib/animations';
import { AnimatedGradientText } from '@/components/ui/animated-gradient-text';
import AboutHero from '@/components/AboutHero';
import AboutStory from '@/components/about/AboutStory';
import AboutProtocol from '@/components/about/AboutProtocol';
import AboutPortfolio from '@/components/about/AboutPortfolio';
import AboutTestimonialsMission from '@/components/about/AboutTestimonialsMission';
import AboutAntiPersona from '@/components/about/AboutAntiPersona';
import AboutFAQ from '@/components/about/AboutFAQ';
import AboutFinalCTA from '@/components/about/AboutFinalCTA';

export default function About() {
  const CTA_DEMO_CLASSES =
    'inline-flex items-center justify-center gap-0 rounded-2xl font-poppins tracking-wide font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0000FF] min-h-[52px] px-16 py-5 text-xl relative overflow-hidden transition-transform transition-colors duration-300 ease-out select-none text-white hover:text-white active:text-white';
  const shimmerBackground = 'linear-gradient(180deg, #F3F7FF 0%, #2F62FF 45%, #0037FF 100%)';
  const borderGlowGradient =
    'linear-gradient(135deg, rgba(102, 178, 255, 0.95), rgba(160, 118, 255, 0.9), rgba(102, 178, 255, 0.95))';
  const MotionLink = motion(Link);
  // Parallax engine (aligned with Home/Info)
  type Axis = 'y' | 'x' | 'both';
  interface ParallaxOptions {
    speed?: number;
    axis?: Axis;
    maxTranslate?: number;
    rootMargin?: string;
  }
  interface ParallaxItem {
    element: HTMLElement;
    speed: number;
    axis: Axis;
    maxTranslate: number;
    inView: boolean;
    lastTransform: string;
  }
  const parallaxItems = useRef(new Set<ParallaxItem>()).current;
  const parallaxIoRef = useRef<IntersectionObserver | null>(null);
  const parallaxRafIdRef = useRef<number | null>(null);
  const parallaxRunningRef = useRef(false);
  let prefersReducedMotion =
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ensureParallaxIO = (rootMargin: string) => {
    if (parallaxIoRef.current) return;
    parallaxIoRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const item = [...parallaxItems].find((it) => it.element === entry.target);
          if (!item) continue;
          item.inView = entry.isIntersecting;
        }
        requestParallaxTick();
      },
      { root: null, threshold: 0, rootMargin },
    );
  };
  const onParallaxScroll = () => requestParallaxTick();
  const onParallaxResize = () => requestParallaxTick();
  const startParallax = () => {
    if (parallaxRunningRef.current) return;
    parallaxRunningRef.current = true;
    window.addEventListener('scroll', onParallaxScroll, { passive: true });
    window.addEventListener('resize', onParallaxResize, { passive: true });
    requestParallaxTick();
  };
  const stopParallax = () => {
    if (!parallaxRunningRef.current) return;
    window.removeEventListener('scroll', onParallaxScroll);
    window.removeEventListener('resize', onParallaxResize);
    parallaxRunningRef.current = false;
  };
  const requestParallaxTick = () => {
    if (parallaxRafIdRef.current !== null) return;
    parallaxRafIdRef.current = window.requestAnimationFrame(updateParallax);
  };
  const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);
  const updateParallax = () => {
    parallaxRafIdRef.current = null;
    if (prefersReducedMotion) {
      parallaxItems.forEach((item) => {
        if (item.lastTransform !== '') {
          item.element.style.transform = '';
          item.lastTransform = '';
        }
      });
      return;
    }
    const viewportHeight = window.innerHeight || 0;
    const viewportWidth = window.innerWidth || 0;
    const centerY = viewportHeight / 2;
    const centerX = viewportWidth / 2;
    const farTop = -viewportHeight * 1.5;
    const farBottom = viewportHeight * 2.5;
    parallaxItems.forEach((item) => {
      const rect = item.element.getBoundingClientRect();
      if (rect.bottom < farTop || rect.top > farBottom) return;
      const elementCenterY = rect.top + rect.height / 2;
      const elementCenterX = rect.left + rect.width / 2;
      const dy = elementCenterY - centerY;
      const dx = elementCenterX - centerX;
      const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
      const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);
      let transform = '';
      if (item.axis === 'y') {
        transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
      } else if (item.axis === 'x') {
        transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
      } else {
        transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
      }
      if (transform !== item.lastTransform) {
        item.element.style.transform = transform;
        item.lastTransform = transform;
      }
    });
  };
  function useParallaxRef(options: ParallaxOptions = {}) {
    const {
      speed = 0.06,
      axis = 'y',
      maxTranslate = 40,
      rootMargin = '20% 0px 20% 0px',
    } = options;
    const elRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
      const el = elRef.current as HTMLElement | null;
      if (!el) return;
      const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
      const onChange = () => {
        prefersReducedMotion = mql.matches;
        requestParallaxTick();
      };
      mql.addEventListener?.('change', onChange);
      ensureParallaxIO(rootMargin);
      startParallax();
      const item: ParallaxItem = {
        element: el,
        speed,
        axis,
        maxTranslate,
        inView: false,
        lastTransform: '',
      };
      try {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 0;
        const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
        const extendedTop = -vh * vm;
        const extendedBottom = vh * (1 + vm);
        item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
      } catch {
        item.inView = true;
      }
      parallaxItems.add(item);
      parallaxIoRef.current?.observe(el);
      el.style.willChange = 'transform';
      el.style.backfaceVisibility = 'hidden';
      el.style.transformStyle = 'preserve-3d';
      updateParallax();
      return () => {
        mql.removeEventListener?.('change', onChange);
        parallaxIoRef.current?.unobserve(el);
        parallaxItems.delete(item);
        el.style.willChange = '';
        el.style.backfaceVisibility = '';
        el.style.transformStyle = '';
        el.style.transform = '';
        if (parallaxItems.size === 0) {
          stopParallax();
        }
      };
    }, [axis, maxTranslate, rootMargin, speed]);
    return elRef;
  }

  const values = [
    {
      icon: Shield,
      title: 'Security First',
      description:
        'Every transaction runs through a trusted payment provider, for security and peace of mind.',
    },
    {
      icon: Zap,
      title: 'Fast Transfers',
      description:
        'Most domain transfers complete within 24 to 48 hours, with a streamlined process and direct support.',
    },
    {
      icon: Users,
      title: 'Expert Guidance',
      description:
        'Personal, step-by-step guidance so you succeed with the new domain, not just own it.',
    },
  ];

  const stats = [
    { number: '20', label: 'Years Building Businesses' },
    { number: '2', label: 'Marketplaces Built and Sold' },
    { number: '48hr', label: 'Average Transfer Time' },
    { number: '100%', label: 'Secure Transactions' },
  ];

  // Background gradient (match Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const hue = Math.random() * (220 - 205) + 205;
    const saturation = Math.random() * (78 - 65) + 65;
    const lightNearWhite = Math.random() * (98 - 96) + 96;
    const lightBlue = Math.random() * (92 - 88) + 88;
    const alphaSoft1 = Math.random() * (0.1 - 0.06) + 0.06;
    const alphaSoft2 = Math.random() * (0.08 - 0.04) + 0.04;
    const centerX = Math.random() * (70 - 30) + 30;
    const centerY = Math.random() * (45 - 15) + 15;
    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1}) 0%, transparent 60%)`,
      `linear-gradient(180deg, #ffffff 0%, hsla(${hue}, ${saturation}%, ${lightBlue}%, 1) 100%)`,
      `linear-gradient(90deg, hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2}) 0%, transparent 50%, hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2}) 100%)`,
    ].join(', ');
    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const statsGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });
  const valuesTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 40 });
  const valuesSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const valuesGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });
  const storyTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 36 });
  const storyGridRef = useParallaxRef({ speed: 0.07, axis: 'y', maxTranslate: 40 });
  const ctaRef = useParallaxRef({ speed: -0.16, axis: 'y', maxTranslate: 70 });

  // Use reveal once hooks to prevent cards from disappearing after scroll
  const statsReveal = useRevealOnce({ amount: 0.3 });
  const valuesReveal = useRevealOnce({ amount: 0.3 });
  const storyReveal = useRevealOnce({ amount: 0.25 });

  // Helper to merge parallax and reveal refs
  const mergeRefs = useCallback(
    (parallaxRef: React.RefObject<HTMLElement>, revealRef: React.RefObject<HTMLElement>) =>
      (node: HTMLElement | null) => {
        if (parallaxRef && 'current' in parallaxRef) {
          (parallaxRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
        if (revealRef && 'current' in revealRef) {
          revealRef.current = node;
        }
      },
    [],
  );

  return (
    <>
      <Helmet>
        <title>About Attain Domains | Premium Domain Marketplace</title>
        <meta
          name="description"
          content="Learn about Attain Domains - your trusted partner for premium domain acquisition. Secure transfers, expert guidance, and personalized support."
        />
      </Helmet>

      <div className="min-h-screen animate-page-fade">
        {/* New Hero Section with Founder Strip */}
        <AboutHero />

        {/* Section 2: The Operator Advantage Story */}
        <AboutStory />

        {/* Section 3: The Attain Protocol */}
        <AboutProtocol />

        {/* Section 4: Portfolio & Outcomes */}
        <AboutPortfolio />

        {/* Section 5: Testimonials & Mission */}
        <AboutTestimonialsMission />

        {/* Section 6: Anti-Persona Fit Check */}
        <AboutAntiPersona />

        {/* Section 7: FAQ */}
        <AboutFAQ />

        {/* Section 8: Final CTA */}
        <AboutFinalCTA />

        {/* Legacy sections below */}
        <div style={backgroundStyle}>
        {/* Hero Section */}
        <section className="relative py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto text-center">
              <div className="stagger-fade-1">
                <h1
                  ref={heroTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  About{' '}
                  <Link to="/services" className="inline-block hover:opacity-90 transition-opacity">
                    <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                      Attain
                    </AnimatedGradientText>
                  </Link>{' '}
                  <Link to="/domains" className="inline-block hover:opacity-90 transition-opacity">
                    <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                      Domains
                    </AnimatedGradientText>
                  </Link>
                </h1>
                <p
                  ref={heroSubRef as any}
                  className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed mb-6 font-normal"
                >
                  I want to help you be successful
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                ref={mergeRefs(statsGridRef as any, statsReveal.ref)}
                className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto"
                variants={statsContainerVariants}
                initial="hidden"
                animate={statsReveal.controls}
              >
                {stats.map((stat, index) => (
                  <motion.div key={index} variants={statsItemVariants} className="apple-card p-8 text-center">
                    <div className="text-4xl font-bold mb-2 text-primary">{stat.number}</div>
                    <div className="text-muted-foreground">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* How I Work */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2
                  ref={valuesTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  How I Work
                </h2>
                <p
                  ref={valuesSubRef as any}
                  className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal"
                >
                  I keep domain acquisition simple, secure and successful for every client.
                </p>
              </div>

              <motion.div
                ref={mergeRefs(valuesGridRef as any, valuesReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-3 gap-8"
                variants={gridContainerVariants}
                initial="hidden"
                animate={valuesReveal.controls}
              >
                {values.map((value, index) => (
                  <motion.div key={index} variants={getGridItemVariants(index)} className="apple-card p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <value.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-black">{value.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* My Story */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                ref={mergeRefs(storyGridRef as any, storyReveal.ref)}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
                variants={gridContainerVariants}
                initial="hidden"
                animate={storyReveal.controls}
              >
                <motion.div variants={getGridItemVariants(0)}>
                  <h2
                    ref={storyTitleRef as any}
                    className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                  >
                    My Story
                  </h2>
                  <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                    <p>
                      I started Attain Domains on a simple belief: a business deserves a domain name that works as hard as it does. I've watched too many good ideas struggle behind a name nobody remembers.
                    </p>
                    <p>
                      Every domain here is hand-picked to help a business launch faster, convert better and build a stronger brand from day one.
                    </p>
                    <p>
                      When you work with me you're not just buying a domain. You're getting someone who has built and sold businesses on names like these.
                    </p>
                  </div>
                </motion.div>

                <motion.div variants={getGridItemVariants(1)} className="apple-card p-8">
                  <h3 className="text-2xl font-bold mb-6 text-black">Why This Is Different</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span className="text-muted-foreground">
                        Hand-curated domains chosen for clarity and intent
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span className="text-muted-foreground">
                        Secure transfers through a trusted payment provider
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span className="text-muted-foreground">
                        Personalized consulting and guidance
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span className="text-muted-foreground">
                        Flexible payment options including rent-to-own
                      </span>
                    </li>
                  </ul>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 apple-hero-bg relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div ref={ctaRef as any} className="max-w-4xl mx-auto text-center">
              <h2 className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg text-white">
                Ready to Find Your Perfect Domain?
              </h2>
              <p className="text-3xl md:text-4xl text-white/90 mb-12 leading-relaxed font-normal">
                Find the name, then build the business behind it. Start browsing the premium collection today.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <MotionLink
                  to="/contact"
                  className={CTA_DEMO_CLASSES}
                  style={{
                    backgroundImage: `${shimmerBackground}, ${borderGlowGradient}`,
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    backgroundSize: '100% 100%, 200% 200%',
                    backgroundPosition: '0% 0%, 0% 50%',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: 'transparent',
                    boxShadow: '0 14px 34px rgba(14, 48, 140, 0.35)',
                    color: '#FFFFFF',
                  }}
                  whileHover={{
                    scale: 1.04,
                    y: -2,
                    boxShadow: '0 18px 40px rgba(14, 48, 140, 0.45)',
                  }}
                  whileTap={{ scale: 0.99, y: 0 }}
                >
                  Get in Touch
                </MotionLink>

              </div>
            </div>
          </div>
        </section>
        </div>
      </div>
    </>
  );
}
